import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Hero: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-dark-primary">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgb(185, 160, 110, 0.15) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-dark-primary/50 to-dark-primary"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Elegant separator */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: '100px', opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="h-px bg-gold/50 mx-auto mb-12"
        />

        {/* Main title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="font-playfair text-3xl sm:text-4xl md:text-7xl lg:text-8xl font-light text-cream mb-4 sm:mb-6 tracking-wide sm:tracking-wider px-2 sm:px-0"
        >
          {t('hero.title')}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="font-playfair text-lg sm:text-xl md:text-2xl text-gold/90 mb-6 sm:mb-8 italic font-light px-2 sm:px-0"
        >
          {t('hero.subtitle')}
        </motion.p>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="text-sm sm:text-base md:text-lg text-cream-dark max-w-3xl mx-auto mb-8 sm:mb-12 leading-relaxed font-light px-4 sm:px-0"
        >
          {t('hero.description')}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4 sm:px-0"
        >
          <a
            href="#preview-gallery"
            className="group relative px-6 sm:px-10 py-3 sm:py-4 overflow-hidden border border-gold/30 hover:border-gold transition-all duration-500 w-full sm:w-auto text-center"
          >
            <span className="relative z-10 text-cream font-light tracking-wide sm:tracking-widest uppercase text-xs sm:text-sm">
              {t('hero.cta')}
            </span>
            <div className="absolute inset-0 bg-gold/10 transform -skew-x-12 -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
          </a>

          <a
            href="#contact"
            className="group relative px-6 sm:px-10 py-3 sm:py-4 overflow-hidden bg-gold/90 hover:bg-gold transition-all duration-500 w-full sm:w-auto text-center"
          >
            <span className="relative z-10 text-dark-primary font-medium tracking-wide sm:tracking-widest uppercase text-xs sm:text-sm">
              {t('hero.customArt')}
            </span>
          </a>
        </motion.div>

        {/* Elegant separator */}
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: '100px', opacity: 1 }}
          transition={{ duration: 1.5, delay: 1.2, ease: "easeOut" }}
          className="h-px bg-gold/50 mx-auto mt-16"
        />

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center text-cream-dark"
          >
            <span className="text-xs tracking-widest uppercase mb-2 font-light">Scroll</span>
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </motion.div>
        </motion.div>
      </div>

      {/* Artistic elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-gold/5 rounded-full filter blur-3xl animate-float"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gold/5 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '3s' }}></div>
    </section>
  );
};

export default Hero;